import OrderDetailsEditCarrier from '@/components/modals/OrderModal/OrderDetailsEditCarrier';
import { DeleteOutline } from '@mui/icons-material';
import { Button, Tab, Tabs } from '@mui/material';
import { Box } from '@mui/system';
import { UseQueryResult } from '@tanstack/react-query';
import { useMapsGoogleConfigurationQuery } from 'api/queries/config';
import { useDeleteOperatorsOrdersMutation, useOperatorsOrdersQuery } from 'api/queries/operators';
import { useOrdersFilesQuery } from 'api/queries/orders';
import { PricingTransparency } from 'components/modals/PricingTransparencyModal/PricingTransparencyModal';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  colors,
  ConfirmModal,
  FileTypeEnum,
  LegSchemaType,
  OrderDetails,
  PageLoader,
  RideDtoSchemaType,
  RideOfferType,
} from 'remote/global';
import BookingEditExtrasForm from './BookingEditExtrasForm';
import BookingEditGroupLeadForm from './BookingEditGroupLeadForm';
import BookingEditRideJourneyForm from './BookingEditRideJourneyForm';

type Props = {
  isOrderEditAvailable: boolean;
  rideNumber: number;
  orderQuery: UseQueryResult<any>;
  onClose: () => void;
};

enum ActiveTabType {
  RIDE_OVERVIEW = 'ride-overview',
  RIDE_JOURNEY = 'ride-journey',
  RIDE_GROUP_LEAD = 'ride-group-lead',
  RIDE_EXTRAS = 'ride-extras',
  RIDE_PRICING = 'ride-pricing',
  DELETE_BOOKING = 'delete-booking',
}

function BookingExtrasGate({
  bookingNumber,
  ride,
  isOrderEditAvailable,
}: {
  bookingNumber: string;
  isOrderEditAvailable: boolean;
  ride: RideDtoSchemaType;
}) {
  const { data: order, isSuccess: isSuccessOrder } = useOperatorsOrdersQuery(bookingNumber);
  const { data: orderFiles, isSuccess: isSuccessFiles } = useOrdersFilesQuery(bookingNumber);

  if (!isSuccessOrder || !isSuccessFiles)
    return (
      <Box sx={{ my: '3rem' }}>
        <PageLoader />
      </Box>
    );

  const rideDetailsFiles = orderFiles.filter((f) => f.fileType === FileTypeEnum.RIDE_DETAILS);
  const signFiles = orderFiles.filter((f) => f.fileType === FileTypeEnum.RIDE_SIGN);

  return (
    <BookingEditExtrasForm
      isOrderEditAvailable={isOrderEditAvailable}
      bookingNumber={bookingNumber}
      defaultValues={{ ...(order as any), signFiles, rideDetailsFiles }}
      ride={ride}
    />
  );
}

function BookingEditForm({ orderQuery, rideNumber, isOrderEditAvailable, onClose }: Props) {
  const { data: googleConfig } = useMapsGoogleConfigurationQuery();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(ActiveTabType.RIDE_OVERVIEW);
  const order = orderQuery.data;
  const [isConfirmDeleteModalOpen, setIsConfirmDeleteModalOpen] = useState(false);

  const selectedRide = order.rides.find((ride: any) => ride.rideNumber === rideNumber)!;
  const isPricingAvailable = order.type !== RideOfferType.CORPORATE_CHARTER;

  const deleteOrderMutation = useDeleteOperatorsOrdersMutation(order.bookingNumber);

  const handleDeleteBooking = async () => {
    try {
      await deleteOrderMutation.mutateAsync();
      setIsConfirmDeleteModalOpen(false);
      onClose();
    } catch (e) {
      // TODO: handle error
    }
  };

  return (
    <>
      <Box
        component='header'
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          p: ['1rem 1rem 0 1rem'],
          backgroundColor: 'rgba(6, 151, 199, 0.05)',
        }}
      >
        <Tabs
          sx={{ pb: '1rem' }}
          value={activeTab}
          onChange={(_, tab) => setActiveTab(tab)}
          variant='scrollable'
        >
          <Tab label={t('modals.editOrder.tabs.overview')} value={ActiveTabType.RIDE_OVERVIEW} />
          <Tab label={t('modals.editOrder.tabs.ride')} value={ActiveTabType.RIDE_JOURNEY} />
          <Tab label={t('modals.editOrder.tabs.groupLead')} value={ActiveTabType.RIDE_GROUP_LEAD} />
          <Tab label={t('modals.editOrder.tabs.extras')} value={ActiveTabType.RIDE_EXTRAS} />
          {isPricingAvailable && (
            <Tab label={t('modals.editOrder.tabs.pricing')} value={ActiveTabType.RIDE_PRICING} />
          )}
          <Button
            sx={{ ml: 'auto' }}
            disabled={!isOrderEditAvailable}
            size='small'
            variant='text'
            onClick={() => setIsConfirmDeleteModalOpen(true)}
          >
            <DeleteOutline />{' '}
          </Button>
        </Tabs>
      </Box>
      <Box sx={{ width: '100%', p: '1rem' }}>
        {activeTab === ActiveTabType.RIDE_OVERVIEW && (
          <Box sx={{ border: `1px solid ${colors.gray4}`, borderRadius: '1rem' }}>
            <OrderDetails
              orderQuery={orderQuery}
              endSlot={
                <OrderDetailsEditCarrier
                  bookingNumber={order.bookingNumber}
                  rideNumber={rideNumber}
                />
              }
            />
          </Box>
        )}
        {activeTab === ActiveTabType.RIDE_JOURNEY && (
          <BookingEditRideJourneyForm
            isOrderEditAvailable={isOrderEditAvailable}
            defaultValues={{
              legs: (selectedRide?.legs as unknown as LegSchemaType[]) ?? [],
            }}
            rideId={selectedRide?.id}
            googleConfig={googleConfig!}
          />
        )}
        {activeTab === ActiveTabType.RIDE_GROUP_LEAD && (
          <BookingEditGroupLeadForm
            isOrderEditAvailable={isOrderEditAvailable}
            bookingNumber={order.bookingNumber}
            defaultValues={{
              groupLead: order.groupLead!,
            }}
          />
        )}
        {activeTab === ActiveTabType.RIDE_EXTRAS && (
          <BookingExtrasGate
            isOrderEditAvailable={isOrderEditAvailable}
            bookingNumber={order.bookingNumber}
            ride={selectedRide}
          />
        )}
        {isPricingAvailable && activeTab === ActiveTabType.RIDE_PRICING && (
          <PricingTransparency bookingNumber={order.bookingNumber} />
        )}
      </Box>
      <ConfirmModal
        open={isConfirmDeleteModalOpen}
        setOpen={setIsConfirmDeleteModalOpen}
        onClose={() => setIsConfirmDeleteModalOpen(false)}
        onConfirm={handleDeleteBooking}
        title={t('OrderView.cancel.title').toString()}
        description={t('OrderView.cancel.subtitle').toString()}
      />
    </>
  );
}

export default BookingEditForm;
